'use client';

import * as React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { ArrowLeft, Ban, Loader2, ShieldAlert } from 'lucide-react';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'default' | 'destructive';
  /** Optional dark variant for use inside the Studio UI. */
  variant?: 'light' | 'dark' | 'marketplace';
  pending?: boolean;
  onConfirm: () => void | Promise<void>;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'default',
  variant = 'light',
  pending = false,
  onConfirm,
}: Props) {
  const isDark = variant === 'dark';
  const isMarketplace = variant === 'marketplace';

  const overlayClass = isMarketplace
    ? 'fixed inset-0 z-[210] bg-[#080312]/75 backdrop-blur-md'
    : isDark
    ? 'fixed inset-0 z-[210] bg-black/65'
    : 'fixed inset-0 z-[210] bg-ink-950/60 backdrop-blur-sm';

  const contentClass = isMarketplace
    ? 'liquid-glass fixed left-1/2 top-1/2 z-[211] w-[calc(100%-2rem)] max-w-[430px] -translate-x-1/2 -translate-y-1/2 rounded-[2rem] border border-white/15 bg-[linear-gradient(145deg,rgba(56,27,105,0.94),rgba(18,8,36,0.97))] p-6 text-white shadow-[0_32px_100px_rgba(5,1,14,0.7)] sm:p-7'
    : isDark
    ? 'fixed left-1/2 top-1/2 z-[211] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-white/10 bg-[#1a0e2a] p-6 text-white shadow-[0_30px_80px_rgba(0,0,0,0.6)]'
    : 'fixed left-1/2 top-1/2 z-[211] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-ink-950/10 bg-white p-6 shadow-[0_30px_80px_rgba(7,5,12,0.25)]';

  const titleClass = isMarketplace
    ? 'text-2xl font-medium tracking-[-0.025em] text-white'
    : isDark
    ? 'text-lg font-black text-white'
    : 'text-lg font-black text-ink-950';
  const descClass = isMarketplace
    ? 'mt-3 max-w-sm text-sm leading-6 text-white/52'
    : isDark
    ? 'mt-2 text-sm leading-6 text-white/60'
    : 'mt-2 text-sm leading-6 text-ink-500';

  const cancelClass = isMarketplace
    ? 'group inline-flex h-12 w-12 items-center justify-center rounded-full border border-white/15 bg-white/[0.06] text-white/65 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] transition hover:border-white/30 hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-300'
    : isDark
    ? 'inline-flex h-10 items-center justify-center rounded-full border border-white/15 bg-white/10 px-5 text-sm font-bold text-white/80 transition hover:bg-white/15 hover:text-white'
    : 'inline-flex h-10 items-center justify-center rounded-full border border-ink-950/10 bg-white px-5 text-sm font-bold text-ink-700 transition hover:border-brand-400 hover:text-brand-600';

  const confirmClass =
    isMarketplace && tone === 'destructive'
      ? 'group inline-flex h-12 w-12 items-center justify-center rounded-full border border-coral-200/25 bg-gradient-to-br from-coral-400 to-red-600 text-white shadow-[0_10px_30px_rgba(244,91,105,0.28),inset_0_1px_0_rgba(255,255,255,0.24)] transition hover:scale-105 hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral-200 disabled:cursor-wait disabled:opacity-50'
      : tone === 'destructive'
      ? 'inline-flex h-10 items-center justify-center rounded-full bg-red-600 px-5 text-sm font-black text-white shadow-sm transition hover:bg-red-500'
      : isDark
      ? 'inline-flex h-10 items-center justify-center rounded-full bg-brand-500 px-5 text-sm font-black text-white shadow-sm transition hover:bg-brand-600'
      : 'inline-flex h-10 items-center justify-center rounded-full bg-ink-950 px-5 text-sm font-black text-white shadow-sm transition hover:bg-ink-800';

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={overlayClass} />
        <Dialog.Content className={contentClass}>
          {isMarketplace ? (
            <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-full border border-coral-200/20 bg-coral-300/10 text-coral-100">
              <ShieldAlert className="h-5 w-5" aria-hidden="true" />
            </div>
          ) : null}
          <Dialog.Title className={titleClass}>{title}</Dialog.Title>
          {description && (
            <Dialog.Description className={descClass}>
              {description}
            </Dialog.Description>
          )}
          <div className={isMarketplace ? 'mt-7 flex justify-end gap-3' : 'mt-6 flex justify-end gap-2'}>
            <Dialog.Close asChild>
              <button type="button" className={cancelClass} aria-label={cancelLabel} title={cancelLabel}>
                {isMarketplace ? (
                  <>
                    <ArrowLeft className="h-5 w-5 transition group-hover:-translate-x-0.5" aria-hidden="true" />
                    <span className="sr-only">{cancelLabel}</span>
                  </>
                ) : cancelLabel}
              </button>
            </Dialog.Close>
            <button
              type="button"
              onClick={() => void onConfirm()}
              disabled={pending}
              className={confirmClass}
              aria-label={confirmLabel}
              title={confirmLabel}
            >
              {isMarketplace ? (
                <>
                  {pending
                    ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
                    : <Ban className="h-5 w-5" aria-hidden="true" />}
                  <span className="sr-only">{confirmLabel}</span>
                </>
              ) : confirmLabel}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
